import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { WHITELISTED_DOMAINS, WHITELISTED_SUFFIXES } from '../utils/messageFormatter';

const LinkWhitelistPage: React.FC = () => {
  const { t } = useTranslation();
  const domains = [...WHITELISTED_DOMAINS].sort((a, b) => a.localeCompare(b));
  const suffixes = [...WHITELISTED_SUFFIXES].sort((a, b) => a.localeCompare(b));

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        {t('whitelistPage.title', 'Whitelisted Websites')}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {t('whitelistPage.description', 'Only links from the following domains and public suffixes will render as clickable in debates. This list may be updated over time.')}
      </Typography>

      <Typography variant="h6" sx={{ mt: 3 }}>
        {t('whitelistPage.whyTitle', 'Why these sites?')}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('whitelistPage.whyBody', 'Whitelisted domains are for safety, not authority. A tabloid opinion piece can sometimes contain a solid explanation; a prestigious domain can host weak arguments. Links are references, not endorsements.')}
      </Typography>

      <Typography variant="h6" sx={{ mt: 3 }}>
        {t('whitelistPage.howTitle', 'How to use links well')}
      </Typography>
      <List dense>
        <ListItem sx={{ py: 0 }}>
          <ListItemText primary={t('whitelistPage.howBullet1', 'Summarize the core claim you\'re referencing in your own words.')} />
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <ListItemText primary={t('whitelistPage.howBullet2', 'Include at most 2 references per message.')} />
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <ListItemText primary={t('whitelistPage.howBullet3', 'Don\'t offload reading to your opponent. If your point stands only if they read the link, you haven\'t made the argument yet.')} />
        </ListItem>
      </List>

      <Typography variant="h6" sx={{ mt: 3 }}>
        {t('whitelistPage.domains', 'Domains')}
      </Typography>
      <List dense>
        {domains.map((d) => (
          <ListItem key={d} sx={{ py: 0 }}>
            <ListItemText primary={d} />
          </ListItem>
        ))}
      </List>

      <Typography variant="h6" sx={{ mt: 3 }}>
        {t('whitelistPage.suffixes', 'Public Suffixes (any subdomain)')}
      </Typography>
      <List dense>
        {suffixes.map((s) => (
          <ListItem key={s} sx={{ py: 0 }}>
            <ListItemText primary={s} />
          </ListItem>
        ))}
      </List>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {t('whitelistPage.note', 'Note: For safety, only http/https links are allowed, and redirects or shortened links may still be blocked.')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('whitelistPage.disclaimer', 'Whitelisting is about harm reduction, not authority.')}
      </Typography>
    </Box>
  );
};

export default LinkWhitelistPage;

